import { getImageProps, type StaticImageData } from "next/image";
import {
  POPUP_GIFTS,
  POPUP_VARIANT_CONFIG,
  type PopupLayout,
  type PopupVariant,
} from "@/utils/popupOffers";
import { GIFT_BADGES, POPUP_PANELS } from "./popupImages";

/** Mirrors the `split` screen in tailwind.config.ts: the side-by-side layout. */
export const SPLIT_LAYOUT_QUERY =
  "(min-width: 1280px), (min-width: 1024px) and (orientation: landscape)";

/**
 * The `sizes` each popup image renders with. The preload passes the same
 * value, so the browser picks the same resized file the popup then shows.
 */
export const PANEL_IMAGE_SIZES: Record<
  PopupLayout,
  { stacked: string; split: string }
> = {
  card: {
    stacked: "(min-width: 768px) 626px, calc(100vw - 36px)",
    split: "(min-width: 1440px) 672px, 512px",
  },
  fullscreen: { stacked: "100vw", split: "50vw" },
};

export const GIFT_BADGE_SIZES =
  "(min-width: 1920px) 240px, (min-width: 1440px) 200px, (min-width: 768px) 168px, 136px";

const loadImage = async (
  image: StaticImageData,
  sizes: string,
): Promise<void> => {
  const { props } = getImageProps({ src: image, alt: "", fill: true, sizes });
  const preload = new window.Image();
  preload.fetchPriority = "high";
  preload.sizes = sizes;
  if (props.srcSet) preload.srcset = props.srcSet;
  preload.src = props.src;
  try {
    await preload.decode();
  } catch (error) {
    console.error("Failed to preload a popup image:", error);
  }
};

/** Downloads and decodes the one panel photo this viewport will show. */
export const preloadPanel = (variant: PopupVariant): Promise<void> => {
  const sizes = PANEL_IMAGE_SIZES[POPUP_VARIANT_CONFIG[variant].layout];
  const panel = POPUP_PANELS[variant];
  return window.matchMedia(SPLIT_LAYOUT_QUERY).matches
    ? loadImage(panel.desktop, sizes.split)
    : loadImage(panel.mobile, sizes.stacked);
};

/** Variant E's reveal shows one of these; all four are small. */
export const preloadGiftBadges = async (): Promise<void> => {
  await Promise.all(
    POPUP_GIFTS.map((gift) => loadImage(GIFT_BADGES[gift], GIFT_BADGE_SIZES)),
  );
};
